import React, { ElementType, ReactNode, Children } from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { Box } from '../Box';
import { spaces } from '../../settings';
import styled from 'styled-components';
import { ResponsiveProp, resolveResponsiveProp } from '../../lib/responsiveProps';
import { joinAndFilter } from '../../lib/joinAndFilter';

export interface InlineProps {
    /**
     * Changes the element for both the bounding box and the individual wrapping boxes
     */
    as?: ElementType<React.HTMLAttributes<HTMLElement>>;
    children: ReactNode;
    noWrap?: boolean;
    space: ResponsiveProp<spaces.Space>;
}

const NegativeBox = styled.div<InlineProps>`
    display: inline-flex;
    align-items: center;
    flex-wrap: ${({ noWrap = false }) => (noWrap ? 'nowrap' : 'wrap')};

    ${({ space }) => {
        return joinAndFilter(
            resolveResponsiveProp(
                space,
                'margin-top',
                (value) => `-${spaces.sizes[value as spaces.Space]}`
            ),
            resolveResponsiveProp(
                space,
                'margin-left',
                (value) => `-${spaces.sizes[value as spaces.Space]}`
            )
        );
    }}
`;

/**
 * Adds spacing between horizontally placed elements.
 */
export function Inline({ as = 'div', children, noWrap, space, ...rest }: InlineProps) {
    const stackItems = flattenChildren(children);

    return (
        <NegativeBox as={as} noWrap={noWrap} space={space} {...rest}>
            {Children.map(stackItems, (child) =>
                child !== null && child !== undefined ? (
                    <Box as={as} marginTop={space} marginLeft={space}>
                        {child}
                    </Box>
                ) : null
            )}
        </NegativeBox>
    );
}
